import { InputTaskCategory } from './IInputTask';

/** How the source's tasks land in each destination (OP-2489). */
export enum CopyMode {
  /** Replaces the destination mode's (Plan or Actual) tasks in each ticked document; the other mode's are kept. */
  Replace = 1,
  /** Adds the source's tasks and steps to the destination mode's data in each ticked document. */
  AddTo = 2,
  /** Creates a new document in each ticked paddock. */
  AddAsNew = 3,
}

/** Which steps of a ticked task a partial copy takes (OP-2489). */
export enum CopyMatchRule {
  /** The whole task, when any of its steps is in a ticked category. */
  Contains = 1,
  /** Only the steps in ticked categories, with the task's own data. */
  Exact = 2,
}

/** The step categories a copy can be filtered by; each step type belongs to one. */
export enum CopyDataCategory {
  Seed = 1,
  SeedDressing = 2,
  SoilInput = 3,
  PlantProtection = 4,
  Operation = 5,
  Biostimulant = 6,
  Production = 7,
}

/** How the copied dates land in the destination (OP-2521). */
export enum CopyDateOption {
  /** Moved by the years between the source and destination, keeping spans that cross a year. */
  Shift = 1,
  /** Copied as they are. */
  Keep = 2,
  /** Task and step dates cleared. */
  Remove = 3,
}

/** How area and dates are copied (OP-2521). */
export interface ICopyOptions {
  /** True copies the source's task and step areas; false scales them to each destination's area. */
  keepArea: boolean;
  dateOption: CopyDateOption;
}

/** Area scaled to each destination and dates shifted to its year, as in the BPAM tool. */
export const DEFAULT_COPY_OPTIONS: ICopyOptions = {
  keepArea: false,
  dateOption: CopyDateOption.Shift,
};

/** What part of the source is copied; a full copy has everything ticked. */
export interface ICopyDataFilter {
  /** Every category and task is ticked; Replace only takes a full copy. */
  isFullCopy: boolean;
  matchRule: CopyMatchRule;
  /** Ticked categories present in the source. */
  categories: CopyDataCategory[];
  /** The tasks to copy, in source order; the final selection. */
  taskIds: string[];
  /** Copies the source's crop and variety onto each destination document. */
  includeCropAndVariety: boolean;
}

export interface ICopySource {
  inputId: string;
  mode: InputTaskCategory;
  filter: ICopyDataFilter;
}

/** One document to copy into; `inputId` is null when a new document is created. */
export interface ICopyTarget {
  farmId: string;
  paddockId: string;
  inputId: string | null;
}

export interface ICopyDestination {
  /** Year given to any new document. */
  year: number;
  mode: InputTaskCategory;
  targets: ICopyTarget[];
}

/**
 * Everything the copy tool collects before copying. The copy logic (OP-2521)
 * applies it. No source/destination provenance is recorded (OP-2489, comment 63386).
 */
export interface ICopyPaddockDataRequest {
  orgId: string;
  copyMode: CopyMode;
  source: ICopySource;
  destination: ICopyDestination;
  options: ICopyOptions;
}
